import { useEffect, useSyncExternalStore } from 'react'
import { fetchMySetupProfile, type SetupProfile } from '../services/appsync-client'

/**
 * The signed-in user's setup fields from the server: admin requests (Reset
 * setup / Resend consent), cross-device setup state (walkthrough seen,
 * explainers) and the call-recording answer. Keyed by user id; a response that
 * lands after an account switch is dropped. Reset on sign-out.
 */
export type SetupProfileState = {
  status: 'idle' | 'loading' | 'loaded' | 'error'
  userId: string | null
  profile: SetupProfile | null
  /** Epoch ms of the last successful load. */
  loadedAt?: number
}

const IDLE: SetupProfileState = { status: 'idle', userId: null, profile: null }

let state: SetupProfileState = IDLE
let inflight: { userId: string; promise: Promise<void> } | null = null
const listeners = new Set<() => void>()

function setState(next: SetupProfileState) {
  state = next
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getState(): SetupProfileState {
  return state
}

export function getSetupProfileState(): SetupProfileState {
  return state
}

/**
 * Load (or with `force`, re-read) the setup profile for `userId`. A failed
 * refresh keeps what is already known; a failed first load reads as `error`.
 */
export function loadSetupProfile(userId: string, opts: { force?: boolean } = {}): Promise<void> {
  if (inflight && inflight.userId === userId) return inflight.promise
  if (!opts.force && state.userId === userId && state.status === 'loaded') return Promise.resolve()
  const keep = state.userId === userId ? state.profile : null
  if (state.userId !== userId || state.status !== 'loaded') {
    setState({ status: 'loading', userId, profile: keep })
  }
  const promise = fetchMySetupProfile()
    .then((profile) => {
      if (state.userId !== userId) return
      setState({ status: 'loaded', userId, profile: profile ?? { userId }, loadedAt: Date.now() })
    })
    .catch(() => {
      if (state.userId !== userId) return
      if (state.status === 'loaded' || keep) {
        setState({ ...state, status: keep ? 'loaded' : 'error', profile: keep })
        return
      }
      setState({ status: 'error', userId, profile: null })
    })
    .finally(() => {
      if (inflight?.promise === promise) inflight = null
    })
  inflight = { userId, promise }
  return promise
}

/** Merge fields the client just wrote (or read elsewhere) into the loaded profile. */
export function patchSetupProfile(userId: string, patch: Partial<SetupProfile>): void {
  if (state.userId !== userId || !state.profile) return
  setState({ ...state, profile: { ...state.profile, ...patch, userId } })
}

/** Sign-out / account switch. */
export function resetSetupProfile(): void {
  inflight = null
  if (state === IDLE) return
  setState(IDLE)
}

/** Subscribe to the setup profile for `userId` (`null` skips loading and reads as idle). */
export function useSetupProfile(userId: string | null): SetupProfileState {
  const snapshot = useSyncExternalStore(subscribe, getState, getState)
  useEffect(() => {
    if (userId) void loadSetupProfile(userId)
  }, [userId])
  if (!userId || snapshot.userId !== userId) return IDLE
  return snapshot
}
